import type { ICancellable } from 'bz-utilities/global/function';
import React from 'react';

import useConst from '../const';
import { useStableAction, useStableCb } from '../stable';
import useUnmountEffect from '../unmount-effect';
import useUpdateEffect from '../update-effect';

type TFunc<P extends any[], R> = (...args: P) => R;

export const useDebounce = <P extends any[], R>(
  cb: TFunc<P, R>,
  deps: React.DependencyList,
  timeout?: number
): TFunc<P, void> & ICancellable => {
  if (useConst(timeout) != timeout) {
    throw new Error('[useDebounce] - timeout has changed');
  }

  const debounced = React.useCallback(useStableCb(cb, deps).bzDebounce(timeout), []);

  useUnmountEffect(() => debounced.cancel());

  return debounced;
};

export const useDebounceAction = <P extends any[], R>(
  cb: TFunc<P, R>,
  deps: React.DependencyList,
  timeout?: number
): TFunc<P, void> & ICancellable => {
  if (useConst(timeout) != timeout) {
    throw new Error('[useDebounceAction] - timeout has changed');
  }

  const debounced = React.useCallback(useStableAction(cb, deps).bzDebounce(timeout), []);

  useUnmountEffect(() => debounced.cancel());

  return debounced;
};

export const useDebouncedValue = <T extends any>(val: T, timeout?: number): T => {
  const [value, setValue] = React.useState(val);

  const debounce = useDebounce(
    (value: T) => {
      setValue(value);
    },
    [],
    timeout
  );

  useUpdateEffect(() => {
    debounce(val);
  }, [val]);

  return value;
};
